import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/LanguageContext";
import BrandMark from "../components/BrandMark";
import InstallAppButton from "../components/InstallAppButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import PasswordInput from "../components/PasswordInput";
import { homePathForRole } from "../lib/roleRoutes";

export default function LoginPage() {
  const { user, login, twofaLogin } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [twofaPending, setTwofaPending] = useState(null); // { tmp_token } or null
  const [twofaCode, setTwofaCode] = useState("");

  useEffect(() => {
    if (user && typeof user === "object") {
      navigate(homePathForRole(user.role), { replace: true });
    }
  }, [user, navigate]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center br-aurora">
        <div className="font-mono-fin text-xs tracking-[0.32em] uppercase text-[color:var(--br-text-dim)]">
          {t("login.checking_session")}
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.requires_2fa) {
      setTwofaPending({ tmp_token: res.tmp_token });
      setTwofaCode("");
      return;
    }
    const from = location.state?.from?.pathname;
    const dest = homePathForRole(res.user.role);
    navigate(from && from !== "/login" ? from : dest, { replace: true });
  };

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await twofaLogin(twofaPending.tmp_token, twofaCode.trim());
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const from = location.state?.from?.pathname;
    const dest = homePathForRole(res.user.role);
    navigate(from && from !== "/login" ? from : dest, { replace: true });
  };

  return (
    <div className="min-h-screen br-aurora flex items-center justify-center px-6 py-12 relative" data-testid="login-page">
      <div className="absolute inset-0 br-grain" />

      {/* Top-right language switcher */}
      <div className="absolute top-6 right-6 sm:top-8 sm:right-8 z-10">
        <LanguageSwitcher variant="compact" />
      </div>

      <div className="relative max-w-[480px] w-full br-fade-up">
        <div className="flex items-center gap-4 mb-10" data-testid="login-brand">
          <BrandMark size={48} />
          <div>
            <div className="font-serif-display italic text-[1.8rem] text-[color:var(--br-parchment)] leading-none">
              Borealis <span className="text-[color:var(--br-bronze-soft)]">Reserve</span>
            </div>
            <div className="font-mono-fin text-[0.62rem] tracking-[0.36em] uppercase text-[color:var(--br-bronze)] mt-2">
              {t("login.brand_tagline")}
            </div>
          </div>
        </div>

        <div className="br-card br-aurora-ribbon relative px-9 py-10" data-testid="login-card">
          <div className="br-overline">
            {twofaPending ? t("login.twofa_overline") : t("login.overline")}
          </div>
          <h1 className="font-serif-display text-[2.2rem] mt-4 leading-[1.1]">
            {twofaPending ? (
              <>
                {t("login.twofa_heading_a")}{" "}
                <em>{t("login.twofa_heading_em")}</em>{" "}
                {t("login.twofa_heading_b")}
              </>
            ) : (
              <>
                {t("login.heading_a")}{" "}
                <em>{t("login.heading_em")}</em>{" "}
                {t("login.heading_b")}
              </>
            )}
          </h1>
          <p className="text-[0.95rem] text-[color:var(--br-text)] mt-3 leading-relaxed">
            {twofaPending ? t("login.twofa_intro") : t("login.intro")}
          </p>

          {twofaPending ? (
            <form onSubmit={handle2faSubmit} className="mt-8 space-y-6" data-testid="twofa-form">
              <label className="block">
                <span className="br-overline block mb-3">{t("login.twofa_code_label")}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  className="br-input font-mono-fin tracking-[0.4em] text-center text-[1.4rem]"
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  data-testid="twofa-code-input"
                />
              </label>

              {error && (
                <div
                  className="text-[0.85rem] py-3 px-4 border"
                  style={{ borderColor: "var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.06)" }}
                  data-testid="twofa-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="br-btn-primary w-full"
                disabled={loading || twofaCode.length !== 6}
                data-testid="twofa-submit-button"
              >
                {loading ? t("login.cta_loading") : t("login.twofa_cta")}
              </button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setTwofaPending(null);
                    setTwofaCode("");
                    setError("");
                  }}
                  className="font-mono-fin text-[0.66rem] tracking-[0.22em] uppercase text-[color:var(--br-text-dim)] hover:text-[color:var(--br-bronze-soft)] transition-colors"
                  data-testid="twofa-back"
                >
                  ← {t("login.twofa_back")}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="login-form">
            <label className="block">
              <span className="br-overline block mb-3">{t("login.email")}</span>
              <input
                type="email"
                className="br-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                data-testid="login-email-input"
              />
            </label>
            <label className="block">
              <span className="br-overline block mb-3">{t("login.password")}</span>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
              />
            </label>

            {error && (
              <div
                className="text-[0.85rem] py-3 px-4 border"
                style={{ borderColor: "var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.06)" }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="br-btn-primary w-full mt-2"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? t("login.cta_loading") : t("login.cta")}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="font-mono-fin text-[0.66rem] tracking-[0.22em] uppercase text-[color:var(--br-text-dim)] hover:text-[color:var(--br-bronze-soft)] transition-colors"
                data-testid="forgot-password-trigger"
              >
                {t("login.forgot")}
              </button>
            </div>
          </form>
          )}

          <div className="mt-8 pt-6 border-t border-[color:var(--br-line)] text-[0.78rem] text-[color:var(--br-text-dim)] leading-relaxed">
            {t("login.no_invite_q")} <br />
            {t("login.no_invite_a")}{" "}
            <a href="mailto:info@borealisreserve.com" className="text-[color:var(--br-bronze-soft)] hover:text-[color:var(--br-bronze)]">
              info@borealisreserve.com
            </a>
          </div>
        </div>

        <div className="text-center mt-6">
          <InstallAppButton style={{ padding: "10px 18px" }} />
        </div>

        <div className="text-center mt-8 text-[0.7rem] font-mono-fin tracking-[0.22em] uppercase text-[color:var(--br-text-dim)]">
          {t("login.footer_brand")}
        </div>
      </div>

      <ForgotPasswordModal open={showForgot} onClose={() => setShowForgot(false)} />
    </div>
  );
}
